import commonActionTypes from '../actionTypes/common.actionTypes'
import { ChartTypeInfo } from '../pages/chartPage/ChartPage'
import { Security, SecurityExpression } from '../proto/generated/security'
import { Exchange, TimeBucket } from '../proto/generated/common'
import { SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import { TimeZone } from '../util/dateTime'
import { WidgetListItems } from '../util/sidebar'
import { defaultExtendedOptionChainColumns, defaultOptionChainColumns } from '../selectors/home'
import { timeIntervalValues } from '../util/tradePage'
import { OptionChainViewKey, ViewType } from '../assets/constants'
import { OptionChainTableView } from '../pages/home/fnoHeader/optionsChainSettingsDialog'
import { isEmbeddedModeSet } from '../util/general'


export interface ActionType {
    type: string;
    payload: any;
}
const getSystemTheme = () => window.matchMedia('(prefers-color-scheme: Dark)').matches;

export interface PaneMetaInfo {
    indicatorCodes: string[],
    indicatorValue: {
        [key: string]: {
            [key: string]: string | boolean
        }
    }
    primary: boolean
}

export interface ComparableSecurityType {
    code: string,
    exchange: Exchange,
    displayName: string,
    count: number
}

export type RecentExpression = Omit<SecurityExpression, 'name'> & { lastUsedTime: number }

export type StrategyExpressionMetaDataType = Pick<SecurityExpression, 'expression' | 'name'>

export interface ChartInfo {
    timeBucket: SuperChartConfiguration_TimeBucket,
    chartTypeInfo: ChartTypeInfo,
    chartTypeUpdated: boolean,
    comparableSecurities: ComparableSecurityType[]
    panesMetaInfo: PaneMetaInfo[]
}

export interface CommonStateType {
    timezone: TimeZone
    apiError: boolean
    errorMessage: string
    errorType: 'error' | 'success'
    darkTheme: boolean
    themeMode: string
    rightSideWidget: WidgetListItems | null
    watchlistId: string | undefined
    showOverviewChart: boolean
    overviewIndexComponentsSortedBy: string
    volumeIndicatorRemoved: boolean
    selectedColumnSet: String
    prevUrl: string
    recentlyUsedExpressions: RecentExpression[]
    recentExpressionsRefreshedTime: number
    strategyExpressionMetaData: StrategyExpressionMetaDataType[]
    optionChain: Record<OptionChainViewKey, {
        normal: {
            timeInterval: TimeBucket
            optionSelectedColumns: string[]
            isMirrorScroll: boolean
        },
        extended: {
            timeInterval: TimeBucket
            optionSelectedColumns: string[]
            isMirrorScroll: boolean
        }
    }>,
    optionChainTableView: OptionChainTableView
    navigationUrls: {
        [ViewType.OPTION_CHAIN]: string
        [ViewType.OPEN_INTEREST]: string
        [ViewType.STRATEGY_BUILDER]: string
        [ViewType.SIMULATOR]: string
    }
    widgetPaneId: number | null
    isFullScreen: false
    optionChainVisibleStrikes: string
    showOptionChainQuantityInLots: boolean
    isNewsCategoryAll: boolean,
    customColors: string[],
    marketDepthSecurity?: Security
}

export const commonInitialState: CommonStateType = {
    timezone: TimeZone.IST,
    watchlistId: undefined,
    apiError: false,
    errorMessage: '',
    errorType: 'error',
    darkTheme: isEmbeddedModeSet() ? localStorage.getItem("themeMode") === "Dark" : getSystemTheme(),
    themeMode: isEmbeddedModeSet() ? (localStorage.getItem("themeMode") || 'Light') : 'System',
    rightSideWidget: WidgetListItems.OVERVIEW,
    optionChain: {
        [OptionChainViewKey.LIVE]: {
            normal: {
                optionSelectedColumns: defaultOptionChainColumns,
                timeInterval: timeIntervalValues[timeIntervalValues.length - 1].value,
                isMirrorScroll: true
            },
            extended: {
                optionSelectedColumns: defaultExtendedOptionChainColumns,
                timeInterval: timeIntervalValues[timeIntervalValues.length - 1].value,
                isMirrorScroll: true
            }
        },
        [OptionChainViewKey.HISTORICAL]: {
            normal: {
                optionSelectedColumns: defaultOptionChainColumns,
                timeInterval: timeIntervalValues[timeIntervalValues.length - 1].value,
                isMirrorScroll: true
            },
            extended: {
                optionSelectedColumns: defaultExtendedOptionChainColumns,
                timeInterval: timeIntervalValues[timeIntervalValues.length - 1].value,
                isMirrorScroll: true
            }
        }
    },
    optionChainTableView: OptionChainTableView.NORMAL,
    showOverviewChart: false,
    overviewIndexComponentsSortedBy: 'maxChange',
    volumeIndicatorRemoved: false,
    selectedColumnSet: '',
    prevUrl: '',
    recentlyUsedExpressions: [],
    strategyExpressionMetaData: [],
    recentExpressionsRefreshedTime: new Date().getTime(),
    navigationUrls: {
        [ViewType.OPTION_CHAIN]: '',
        [ViewType.OPEN_INTEREST]: '',
        [ViewType.STRATEGY_BUILDER]: '',
        [ViewType.SIMULATOR]: ''
    },
    widgetPaneId: null,
    isFullScreen: false,
    optionChainVisibleStrikes: '15',
    showOptionChainQuantityInLots: true,
    isNewsCategoryAll: true,
    customColors: [],
    marketDepthSecurity: null
}

export default function commonReducer(state = commonInitialState, action: ActionType) {
    const { type, payload } = action
    switch (type) {
        case commonActionTypes.SET_API_ERROR:
            return {
                ...state,
                apiError: payload.error,
                errorMessage: payload.message,
                errorType: payload.errorType
            }
        case commonActionTypes.RESET_INITIAL_STATE:
            return {
                ...commonInitialState
            }
        case commonActionTypes.SET_SELECTECED_WATCHLIST_ID:
            return {
                ...state,
                watchlistId: payload
            }
        case commonActionTypes.SET_SECURITY:
            return {
                ...state,
                security: payload
            }
        case commonActionTypes.SET_TIME_ZONE:
            return {
                ...state,
                timezone: payload
            }
        case commonActionTypes.TOGGLE_THEME:
            return {
                ...state,
                darkTheme: action.payload !== undefined ? action.payload : !state.darkTheme
            }
        case commonActionTypes.SET_THEME_MODE:
            if (action.payload === 'System') {
                return {
                    ...state,
                    themeMode: 'System',
                    darkTheme: getSystemTheme()
                };
            } else {
                return {
                    ...state,
                    themeMode: action.payload,
                    darkTheme: action.payload === 'Dark'
                };
            }
        case commonActionTypes.SET_RIGHT_SIDE_WIDGET:
            return {
                ...state,
                rightSideWidget: payload
            }
        case commonActionTypes.SAVE_OPTION_SELECTED_COLUMN:
            return {
                ...state,
                optionChain: {
                    ...state.optionChain,
                    [payload.optionChainViewKey]: {
                        ...state.optionChain[payload.optionChainViewKey as OptionChainViewKey],
                        [payload.type]: {
                            ...state.optionChain[payload.optionChainViewKey as OptionChainViewKey][payload.type],
                            optionSelectedColumns: payload.columns
                        }
                    }
                }
            }
        case commonActionTypes.SET_CHARTS_META_DATA:
            return {
                ...state,
                chartsMetaData: payload
            }
        case commonActionTypes.SHOW_OVERVIEW_CHART:
            return {
                ...state,
                showOverviewChart: payload
            }
        case commonActionTypes.SET_OVERVIEW_INDEX_COMPONENTS_SORTED_BY:
            return {
                ...state,
                overviewIndexComponentsSortedBy: payload
            }
        case commonActionTypes.VOLUME_INDICATOR_REMOVED:
            return {
                ...state,
                volumeIndicatorRemoved: payload
            }
        case commonActionTypes.ADD_OR_UPDATE_STRATEGY_EXPRESSION_METADATA:
            const newStrategyExpressionsMetaData = (state.strategyExpressionMetaData ?? []).filter((expression) => (expression.expression !== action.payload.expression))
            newStrategyExpressionsMetaData.push({
                name: action.payload.name,
                expression: action.payload.expression
            })
            return {
                ...state,
                strategyExpressionMetaData: newStrategyExpressionsMetaData
            }
        case commonActionTypes.SET_PREV_URL:
            return {
                ...state,
                prevUrl: payload
            }
        case commonActionTypes.SAVE_SCREENER_SELECTED_COLUMN_SET:
            return {
                ...state,
                selectedColumnSet: payload
            }
        case commonActionTypes.SAVE_INTO_RECENT_EXPRESSIONS:
            return {
                ...state,
                recentlyUsedExpressions: payload
            }
        case commonActionTypes.SAVE_RECENT_EXPRESSIONS_REFRESHED_TIME:
            return {
                ...state,
                recentExpressionsRefreshedTime: payload
            }
        case commonActionTypes.DELETE_RECENT_EXPRESSION:
            return {
                ...state,
                recentlyUsedExpressions: [...state.recentlyUsedExpressions].filter((exp) => exp.expression !== payload)
            }
        case commonActionTypes.SET_NAVIGATION_URL:
            return {
                ...state,
                navigationUrls: {
                    ...(state?.navigationUrls ?? {}),
                    [payload.viewType]: payload.url
                }
            }
        case commonActionTypes.MULTI_PANE_FULLSCREEN:
            return {
                ...state,
                container: payload
            }
        case commonActionTypes.SET_WIDGETS_PANE_ID:
            return {
                ...state,
                widgetPaneId: payload
            }
        case commonActionTypes.TOGGLE_FULLSCREEN:
            return {
                ...state,
                isFullScreen: !state.isFullScreen
            }
        case commonActionTypes.SET_OPTION_CHAIN_VISIBLE_STRIKES:
            return {
                ...state,
                optionChainVisibleStrikes: payload
            }
        case commonActionTypes.SET_OPTION_CHAIN_TABLE_VIEW:
            return {
                ...state,
                optionChainTableView: payload
            }
        case commonActionTypes.SET_OPTION_CHAIN_QUANTITY_IN_LOTS:
            return {
                ...state,
                showOptionChainQuantityInLots: payload
            }
        case commonActionTypes.SET_OPTION_CHAIN_TIMEBUCKET:
            return {
                ...state,
                optionChain: {
                    ...state.optionChain,
                    [payload.optionChainViewKey]: {
                        ...state.optionChain[payload.optionChainViewKey as OptionChainViewKey],
                        [payload.type]: {
                            ...state.optionChain[payload.optionChainViewKey as OptionChainViewKey][payload.type],
                            timeInterval: payload.timeBucket
                        }
                    }
                }
            }
        case commonActionTypes.SET_OPTION_CHAIN_MIRROR_SCROLL:
            return {
                ...state,
                optionChain: {
                    ...state.optionChain,
                    [payload.optionChainViewKey]: {
                        ...state.optionChain[payload.optionChainViewKey as OptionChainViewKey],
                        [payload.type]: {
                            ...state.optionChain[payload.optionChainViewKey as OptionChainViewKey][payload.type],
                            isMirrorScroll: payload.isMirrorScroll
                        }
                    }
                }
            }
        case commonActionTypes.SET_NEWS_CATEGORY:
            return {
                ...state,
                isNewsCategoryAll: payload,
            }
        case commonActionTypes.ADD_CUSTOM_COLOR:
            if ((state.customColors || []).includes(payload)) return state; // Avoid duplicates
            return {
                ...state,
                customColors: [...state.customColors, payload]
            }
        case commonActionTypes.UPDATE_MARKET_DEPTH_SECURITY:
                    return {
                        ...state,
                        marketDepthSecurity: payload
                    }
        default:
            return state
    }
}

const updateMarketDepthSecurity = (marketDepthSecurity: Security) => ({
    type: commonActionTypes.UPDATE_MARKET_DEPTH_SECURITY,
    payload: marketDepthSecurity
});

export { updateMarketDepthSecurity } 